import { Timestamp } from "firebase/firestore";

export const NOTIFICATION_HEALTH_ISSUE_CODES = [
  "NO_FCM_TOKEN",
  "MISSING_CHANNEL_RECORD",
  "TOKEN_WITHOUT_ROUTING",
  "STORED_TOPICS_MISMATCH",
  "RECORDED_FCM_TOPICS_MISMATCH",
  "ROUTING_PRESENT_WHILE_DISABLED",
] as const;

export type NotificationHealthIssueCode = (typeof NOTIFICATION_HEALTH_ISSUE_CODES)[number];

type NotificationHealthIssueDetails = {
  label: string;
  fieldName?: "fcmTopics" | "subscribedTopics";
  description: string;
  className: string;
};

const NOTIFICATION_HEALTH_ISSUE_DETAILS: Record<
  NotificationHealthIssueCode,
  NotificationHealthIssueDetails
> = {
  NO_FCM_TOKEN: {
    label: "No FCM token",
    description: "The signed-in user has no registered FCM token.",
    className: "border-rose-200 bg-rose-50 text-rose-700",
  },
  MISSING_CHANNEL_RECORD: {
    label: "Missing routing record",
    description: "The user has a token, but no broadcast routing record.",
    className: "border-amber-200 bg-amber-50 text-amber-800",
  },
  TOKEN_WITHOUT_ROUTING: {
    label: "Token without routing",
    description: "The routing record exists, but it has no stored or recorded topics.",
    className: "border-rose-200 bg-rose-50 text-rose-700",
  },
  STORED_TOPICS_MISMATCH: {
    label: "Routing configuration out of sync",
    fieldName: "fcmTopics",
    description: "The saved routing topics do not match the user's current roles, preferences, or space languages.",
    className: "border-amber-200 bg-amber-50 text-amber-800",
  },
  RECORDED_FCM_TOPICS_MISMATCH: {
    label: "FCM subscription record out of sync",
    fieldName: "subscribedTopics",
    description: "The saved FCM topic record does not match the user's current routing configuration and app language.",
    className: "border-amber-200 bg-amber-50 text-amber-800",
  },
  ROUTING_PRESENT_WHILE_DISABLED: {
    label: "Routing active while disabled",
    description: "Topics are still recorded while notifications should be disabled.",
    className: "border-violet-200 bg-violet-50 text-violet-700",
  },
};

export type NotificationHealth = {
  userId: string;
  issueCodes: NotificationHealthIssueCode[];
  tokenCount: number;
  storedTopicCount: number;
  recordedFcmTopicCount: number;
  expectedStoredTopicCount: number;
  authLastSignInAt: Timestamp | null;
  firstDetectedAt: Timestamp;
  checkedAt: Timestamp;
};

export type RecheckNotificationHealthResponse = {
  userId: string;
  issueCodes: NotificationHealthIssueCode[];
  tokenCount: number;
  storedTopicCount: number;
  recordedFcmTopicCount: number;
  expectedStoredTopicCount: number;
  authLastSignInAt: string | null;
  firstDetectedAt: string | null;
  checkedAt: string;
};

export type NotificationHealthRepairOutcome =
  | "REPAIRED"
  | "NO_CHANGES"
  | "MANUAL_ACTION_REQUIRED"
  | "REPAIR_INCOMPLETE";

export type NotificationHealthRepairAction =
  | "ENSURED_FCM_SUBSCRIPTIONS"
  | "REMOVED_STALE_FCM_SUBSCRIPTIONS"
  | "CREATED_ROUTING_RECORD"
  | "UPDATED_ROUTING_RECORD"
  | "CLEARED_DISABLED_ROUTING";

export type RepairNotificationHealthResponse =
  RecheckNotificationHealthResponse & {
    outcome: NotificationHealthRepairOutcome;
    beforeIssueCodes: NotificationHealthIssueCode[];
    actions: NotificationHealthRepairAction[];
    ensuredFcmTopics: string[];
    removedFcmTopics: string[];
  };

const NOTIFICATION_HEALTH_REPAIR_OUTCOME_DETAILS: Record<
  NotificationHealthRepairOutcome,
  { label: string; description: string; className: string }
> = {
  REPAIRED: {
    label: "Routing repaired",
    description: "The stored routing was reconciled and has no current exceptions.",
    className: "border-emerald-200 bg-emerald-50 text-emerald-800",
  },
  NO_CHANGES: {
    label: "No repair needed",
    description: "No current notification-routing exception requires repair.",
    className: "border-sky-200 bg-sky-50 text-sky-800",
  },
  MANUAL_ACTION_REQUIRED: {
    label: "User action required",
    description: "The issue cannot be repaired by the backend, such as when no FCM token is registered.",
    className: "border-amber-200 bg-amber-50 text-amber-900",
  },
  REPAIR_INCOMPLETE: {
    label: "Repair needs attention",
    description: "The repair ran, but current exceptions remain and should be reviewed.",
    className: "border-rose-200 bg-rose-50 text-rose-800",
  },
};

const NOTIFICATION_HEALTH_REPAIR_ACTION_LABELS: Record<
  NotificationHealthRepairAction,
  string
> = {
  ENSURED_FCM_SUBSCRIPTIONS: "FCM subscriptions refreshed",
  REMOVED_STALE_FCM_SUBSCRIPTIONS: "Stale FCM subscriptions removed",
  CREATED_ROUTING_RECORD: "Routing record created",
  UPDATED_ROUTING_RECORD: "Routing record updated",
  CLEARED_DISABLED_ROUTING: "Disabled routing cleared",
};

export const getNotificationHealthIssueDetails = (
  issueCode: NotificationHealthIssueCode
) => NOTIFICATION_HEALTH_ISSUE_DETAILS[issueCode];

export const getNotificationHealthRepairOutcomeDetails = (
  outcome: NotificationHealthRepairOutcome
) => NOTIFICATION_HEALTH_REPAIR_OUTCOME_DETAILS[outcome];

export const getNotificationHealthRepairActionLabel = (
  action: NotificationHealthRepairAction
) => NOTIFICATION_HEALTH_REPAIR_ACTION_LABELS[action];
